import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { rateColumns, goalieRateColumns, positionMap } from "../constants/ratings";
import {
  codeToDisplay,
  getLineupAverages,
  getLinePlayers,
  getRatingColor,
  getRatingRange,
  getGoalieRatingRange
} from "../utils/helpers";
import { filterSkaters, filterGoalies } from "../utils/filterPlayers";
import { resolveRosterTeamFields, resolveGoalieRosterTeamFields, ROSTER_AF_COLUMN_INDEX, ROSTER_GOALIE_Z_COLUMN_INDEX } from "../utils/rosterHelpers";
import {
  buildLineupFromTeam,
  getRosterTeamOptions,
  isRosteredOnTeam,
  isRosteredGoalieOnTeam,
  loadSavedUserTeam,
  saveUserTeam,
} from "../utils/buildTeamLineup";
import { getCachedRoster, setCachedRoster } from "../utils/rosterCache";
import { loadPersistedFilters, savePersistedFilters } from "../utils/persistFilters";
import { getLineChemistry } from "../utils/lineupAnalysis";
import {
  formatLineupText,
  copyTextToClipboard,
  downloadTextFile,
  downloadLineupImage,
} from "../utils/lineupExport";
import { devLog } from "../utils/devLog";
import {
  getAssignmentLabel,
  getGoalieAssignmentLabel,
  buildPlayerAssignmentMap,
  buildGoalieAssignmentMap
} from "../utils/assignments";
import { useAuth } from "../contexts/AuthContext";
import FreeAgentWishlist from "./FreeAgentWishlist";
import { lineupService } from "../services/lineupService";
import PlayerCard from "./PlayerCard";
import GoalieCard from "./GoalieCard";
import PlayerNameBadges from "./PlayerNameBadges";
import LineupAnalysisPanel, { LineChemistryBadge } from "./LineupAnalysisPanel";
import PlayerCompareModal from "./PlayerCompareModal";
import RosterLoadingSkeleton from "./RosterLoadingSkeleton";
import { applyFaStatusToPlayers, applyFaStatusToPlayer, loadFaStatusMaps } from "../utils/faStatusHelpers";
import VirtualizedPlayerGrid from "./VirtualizedPlayerGrid";
import VirtualizedPlayerTable from "./VirtualizedPlayerTable";
import VirtualizedGoalieTable from "./VirtualizedGoalieTable";

const AuthModal = lazy(() => import("./Auth/AuthModal"));
const LineupManagerModal = lazy(() => import("./LineupManager/LineupManagerModal"));

// Position display mapping for lineup positions
const lineupPositionMap = {
  'LW': 'Left Wing',
  'C': 'Center', 
  'RW': 'Right Wing',
  'D1': 'Defense',
  'D2': 'Defense'
};

const WISHLIST_STORAGE_KEY = "tugg_wishlist_v1";

function loadWishlistFromStorage() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return { players: [], goalies: [], salaries: {} };
}

const HockeyLineupBuilder = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const persistedFilters = useMemo(() => loadPersistedFilters(), []);
  const [players, setPlayers] = useState([]);
  // const [fileName, setFileName] = useState("No file selected");
  const [position, setPosition] = useState(persistedFilters.position);
  const [team, setTeam] = useState(persistedFilters.team);
  const [allTeams, setAllTeams] = useState(["all"]);
  const [error, setError] = useState(null);
  const [isLoadingRoster, setIsLoadingRoster] = useState(true);
  const [dataUpdatedAt, setDataUpdatedAt] = useState(null);
  const [sortColumn, setSortColumn] = useState(persistedFilters.sortColumn);
  const [sortDirection, setSortDirection] = useState(persistedFilters.sortDirection);
  const [playerTypeFilter, setPlayerTypeFilter] = useState(persistedFilters.playerTypeFilter);
  const [theme, setTheme] = useState("dark");
  const [searchTerm, setSearchTerm] = useState(persistedFilters.searchTerm);
  const [playerViewMode, setPlayerViewMode] = useState(persistedFilters.playerViewMode);
  const [goalieViewMode, setGoalieViewMode] = useState(persistedFilters.goalieViewMode);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareSeed, setCompareSeed] = useState(null);
  const [undoCount, setUndoCount] = useState(0);
  const undoStackRef = useRef([]);
  const [exportNotice, setExportNotice] = useState("");
  
  // Auth and Lineup Management State
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [lineupModalOpen, setLineupModalOpen] = useState(false);
  const [currentLoadedLineup, setCurrentLoadedLineup] = useState(null);
  const [quickSaveLoading, setQuickSaveLoading] = useState(false);
  const [quickSavePromptOpen, setQuickSavePromptOpen] = useState(false);
  const [quickSaveLineupName, setQuickSaveLineupName] = useState('');
  
  // Drag and drop state
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  
  // Position search state
  const [positionSearches, setPositionSearches] = useState({});
  const [positionSearchResults, setPositionSearchResults] = useState({});

  const [forwards, setForwards] = useState([
    { LW: null, C: null, RW: null },
    { LW: null, C: null, RW: null },
    { LW: null, C: null, RW: null },
    { LW: null, C: null, RW: null },
    { LW: null, C: null, RW: null }
  ]);
  const [defensemen, setDefensemen] = useState([
    { D1: null, D2: null },
    { D1: null, D2: null },
    { D1: null, D2: null },
    { D1: null, D2: null }
  ]);
  const [lineupGoalies, setLineupGoalies] = useState([
    null, null, null
  ]);
  const [salaries, setSalaries] = useState({});
  // const [maxSalary, setMaxSalary] = useState(95500000);
  const [goalies, setGoalies] = useState([]);
  const [salaryCap, setSalaryCap] = useState(104000000);
  const [retainedSalary, setRetainedSalary] = useState(0);
  // const [totalLineupSalary, setTotalLineupSalary] = useState(0);
  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, player: null });
  
  // State to track which lineup players are showing their rates instead of names
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());

  const [activeView, setActiveView] = useState("players");
  const [selectedUserTeam, setSelectedUserTeam] = useState("");

  // Wishlist state
  const initialWishlist = loadWishlistFromStorage();
  const [wishlistPlayers, setWishlistPlayers] = useState(initialWishlist.players);
  const [wishlistGoalies, setWishlistGoalies] = useState(initialWishlist.goalies);
  const [wishlistSalaries, setWishlistSalaries] = useState(initialWishlist.salaries || {});

  const filterParams = useMemo(
    () => ({ position, team, searchTerm, sortColumn, sortDirection, playerType: playerTypeFilter }),
    [position, team, searchTerm, sortColumn, sortDirection, playerTypeFilter]
  );

  const filteredPlayers = useMemo(
    () => filterSkaters(players, filterParams),
    [players, filterParams]
  );

  const filteredGoalies = useMemo(
    () => filterGoalies(goalies, filterParams),
    [goalies, filterParams]
  );

  const rosterTeamOptions = useMemo(
    () => getRosterTeamOptions(players, goalies),
    [players, goalies]
  );

  useEffect(() => {
    if (user?.uid) {
      setSelectedUserTeam(loadSavedUserTeam(user.uid));
    } else {
      setSelectedUserTeam("");
    }
  }, [user?.uid]);

  const playerAssignmentMap = useMemo(
    () => buildPlayerAssignmentMap(forwards, defensemen),
    [forwards, defensemen]
  );

  const goalieAssignmentMap = useMemo(
    () => buildGoalieAssignmentMap(lineupGoalies),
    [lineupGoalies]
  );

  const playerRatingRange = useMemo(
    () => getRatingRange(filteredPlayers, rateColumns),
    [filteredPlayers]
  );

  const goalieRatingRange = useMemo(
    () => getGoalieRatingRange(filteredGoalies, goalieRateColumns.map((col) => col.name)),
    [filteredGoalies]
  );

  const loadDataFromGoogleSheets = async (sheetId, sheetName) => {
    const url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(sheetName)}`;

    devLog('Loading Google Sheet tab:', sheetName);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Could not access Google Sheets tab "${sheetName}"`);
    }

    const csvText = await response.text();
    if (!csvText || csvText.includes('INTERNAL_ERROR') || csvText.includes('<HTML>')) {
      throw new Error(`Could not access Google Sheets tab "${sheetName}"`);
    }

    const rows = csvText.split(/\r?\n/).filter(Boolean).map(row => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current);
      return result;
    });

    return rows.slice(1);
  };

  const loadExcelFaSheetRows = async (sheetName) => {
    const response = await fetch('/db.xlsx');
    const buffer = await response.arrayBuffer();
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(buffer, { type: 'array' });
    const faSheet =
      workbook.Sheets[sheetName] ||
      workbook.Sheets[sheetName.toLowerCase()] ||
      workbook.Sheets[sheetName.toUpperCase()];
    if (!faSheet) {
      throw new Error(`${sheetName} sheet missing from db.xlsx`);
    }
    const faData = XLSX.utils.sheet_to_json(faSheet, { header: 1 });
    return faData.slice(1);
  };

  const GOOGLE_SHEET_ID = '1HskW2DOmWGIzKuafDEQdsqFftPU3s7Y-57J2kpC46y4';

  const loadFaStatusMapsFromSources = () =>
    loadFaStatusMaps({
      googleSheetId: GOOGLE_SHEET_ID,
      loadGoogleSheetRows: loadDataFromGoogleSheets,
      loadExcelSheetRows: loadExcelFaSheetRows,
    });

  const applyTeamListFromRoster = (playerList, goalieList) => {
    const teamSet = new Set([
      ...playerList.map((player) => player.team),
      ...goalieList.map((goalie) => goalie.team),
    ]);
    if (
      playerList.some((player) => player.isFreeAgent) ||
      goalieList.some((goalie) => goalie.isFreeAgent)
    ) {
      teamSet.add("Free Agents");
    }
    setAllTeams(["all", ...[...teamSet].sort()]);
  };

  // Load data function - Google Sheets approach
  const loadData = async () => {
    setIsLoadingRoster(true);
    try {
    const { skaterMap, goalieMap } = await loadFaStatusMapsFromSources();
    devLog('FA statuses loaded - skaters:', skaterMap.size, 'goalies:', goalieMap.size);

    const cached = getCachedRoster();
    if (cached) {
      const playersWithFaStatus = applyFaStatusToPlayers(cached.players, skaterMap);
      const goaliesWithFaStatus = cached.goalies.map((goalie) =>
        applyFaStatusToPlayer(goalie, goalieMap)
      );

      setPlayers(playersWithFaStatus);
      setGoalies(goaliesWithFaStatus);
      applyTeamListFromRoster(playersWithFaStatus, goaliesWithFaStatus);
      setError(null);
      setDataUpdatedAt(new Date());
      devLog('Loaded roster from session cache with FA statuses');
      return;
    }

    devLog('Starting loadData with Google Sheets...');
    try {
      let playersData = [];
      let goaliesData = [];
      
      // Try Google Sheets first
      try {
        devLog('Loading from Google Sheets...');

        const playersRows = await loadDataFromGoogleSheets(GOOGLE_SHEET_ID, 'Skaters');
        const goalieRows = await loadDataFromGoogleSheets(GOOGLE_SHEET_ID, 'Goalies');

        devLog('Google Sheets data loaded - Players:', playersRows.length, 'Goalies:', goalieRows.length);
        
        // Debug: Log first few rows to see data structure
        devLog('First player row:', playersRows[0]);
        devLog('First player row CK value (index 10):', playersRows[0] ? playersRows[0][10] : 'no data');
        
        // Convert to the same format as Excel
        playersData = playersRows.map((row, idx) => {
          const player = {
            'Player Name': row[2] || '',
            'Team Name': row[3] || '',
            'C': row[4] === 'X' ? 'X' : '',
            'L': row[5] === 'X' ? 'X' : '',
            'R': row[6] === 'X' ? 'X' : '',
            'D': row[7] === 'X' ? 'X' : '',
            'CON': row[8] || 0,  // Column I
            'CK': row[10] || 0,  // Column K  
            'FG': row[11] || 0,  // Column L
            'DI': row[12] || 0,  // Column M
            'SK': row[13] || 0,  // Column N
            'ST': row[14] || 0,  // Column O
            'EN': row[15] || 0,  // Column P
            'DU': row[16] || 0,  // Column Q
            'PH': row[17] || 0,  // Column R
            'FO': row[18] || 0,  // Column S
            'PA': row[19] || 0,  // Column T
            'SC': row[20] || 0,  // Column U
            'DF': row[21] || 0,  // Column V
            'PS': row[22] || 0,  // Column W
            'EX': row[23] || 0,  // Column X
            'LD': row[24] || 0,  // Column Y
            'OV': row[27] || 0,  // Column AB
            'Age': row[30] || 0,  // Column AE
            'AF': row[ROSTER_AF_COLUMN_INDEX] || 0,  // Column AF
            'SP': row[32] || 0  // Salary column from Google Sheets CSV (index 32)
          };
          
          // Debug: Log CK value for first few players
          if (idx < 3) {
            devLog(`Player ${idx} (${player['Player Name']}): CK = ${player.CK}, raw value = ${row[10]}`);
          }
          
          return player;
        }).filter(player => player['Player Name']);
        
        goaliesData = goalieRows.map((row, idx) => {
          // Debug: Log raw row data for first few goalies or Bobrovsky specifically
          if (idx < 5 || (row[2] && row[2].toLowerCase().includes('bobrovsky'))) {
            devLog(`Raw goalie row ${idx}: length=${row.length}, col23(X/SP)="${row[23]}", col26(AA/Salary)="${row[26]}", name="${row[2]}"`);
          }
          
          return {
            'Goalie Name': row[2] || '',
            'Team Name': row[3] || '',
            'CON': row[4] || 0,
            'SK': row[6] || 0,
            'DU': row[7] || 0,
            'EN': row[8] || 0,
            'SZ': row[9] || 0,
            'AG': row[10] || 0,
            'RB': row[11] || 0,
            'SC': row[12] || 0,
            'HS': row[13] || 0,
            'RT': row[14] || 0,
            'PH': row[15] || 0,
            'PS': row[16] || 0,
            'EX': row[17] || 0,
            'LD': row[18] || 0,
            'PO': row[19] || 0,
            'MO': row[20] || 0,
            'OV': row[21] || 0,
            'Age': row[24] || 0,  // Column Y
            'Z': row[ROSTER_GOALIE_Z_COLUMN_INDEX] || 0,  // Column Z — roster flag
            'SP': row[23] || 0,  // Column X for SP
            'Salary': row[26] || 0  // Column AA for Salary
          };
        }).filter(goalie => goalie['Goalie Name']);
        
      } catch (sheetsError) {
        devLog('Google Sheets failed, falling back to Excel:', sheetsError.message);
        // Don't throw here, let it fall through to Excel fallback
      }
      
      // Fallback to Excel if Google Sheets fails or returns no data
      if (playersData.length === 0 || goaliesData.length === 0) {
        try {
          devLog('Loading Excel fallback...');
          const response = await fetch('/db.xlsx');
          const buffer = await response.arrayBuffer();
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(buffer, { type: 'array' });
          devLog('Available sheets:', Object.keys(workbook.Sheets));
          
          const skaterSheet = workbook.Sheets["Skaters"];
          const goalieSheet = workbook.Sheets["Goalies"];
          devLog('Skater sheet exists:', !!skaterSheet);
          devLog('Goalie sheet exists:', !!goalieSheet);

          const skaterData = XLSX.utils.sheet_to_json(skaterSheet, { header: 1 });
          const skaterRows = skaterData.slice(1);
          devLog('Skater rows:', skaterRows.length);
          
          let goalieRows = [];
          if (goalieSheet) {
            const goalieData = XLSX.utils.sheet_to_json(goalieSheet, { header: 1 });
            goalieRows = goalieData.slice(1);
            devLog('Goalie rows:', goalieRows.length);
          } else {
            devLog('No goalie sheet found');
          }
          
          // Convert Excel data to same format as Google Sheets parsing
          playersData = skaterRows.map((row, idx) => {
            if (!row || row.length < 33) return null;
            
            const player = {
              'Player Name': row[2] || '',
              'Team Name': row[3] || '',
              'C': row[4] === 'X' ? 'X' : '',
              'L': row[5] === 'X' ? 'X' : '',
              'R': row[6] === 'X' ? 'X' : '',
              'D': row[7] === 'X' ? 'X' : '',
              'CON': row[8] || 0,  // Column I
              'CK': row[10] || 0,  // Column K  
              'FG': row[11] || 0,  // Column L
              'DI': row[12] || 0,  // Column M
              'SK': row[13] || 0,  // Column N
              'ST': row[14] || 0,  // Column O
              'EN': row[15] || 0,  // Column P
              'DU': row[16] || 0,  // Column Q
              'PH': row[17] || 0,  // Column R
              'FO': row[18] || 0,  // Column S
              'PA': row[19] || 0,  // Column T
              'SC': row[20] || 0,  // Column U
              'DF': row[21] || 0,  // Column V
              'PS': row[22] || 0,  // Column W
              'EX': row[23] || 0,  // Column X
              'LD': row[24] || 0,  // Column Y
              'OV': row[27] || 0,  // Column AB
              'Age': row[30] || 0,  // Column AE
              'AF': row[ROSTER_AF_COLUMN_INDEX] || 0,  // Column AF
              'SP': row[32] || 0
            };
            
            // Debug: Log CK value for first few players from Excel
            if (idx < 3) {
              devLog(`Excel Player ${idx} (${player['Player Name']}): CK = ${player.CK}, raw value = ${row[10]}`);
            }
            
            return player;
          }).filter(Boolean);
          
          goaliesData = goalieRows.map((row, idx) => {
            // Debug: Log raw Excel row data for first few goalies or Bobrovsky specifically
            if (idx < 5 || (row[2] && row[2].toLowerCase().includes('bobrovsky'))) {
              devLog(`Raw Excel goalie row ${idx}: length=${row.length}, col23(X/SP)="${row[23]}", col26(AA/Salary)="${row[26]}", name="${row[2]}"`);
            }
            
            return {
              'Goalie Name': row[2] || '',
              'Team Name': row[3] || '',
              'CON': row[4] || 0,
              'SK': row[6] || 0,
              'DU': row[7] || 0,
              'EN': row[8] || 0,
              'SZ': row[9] || 0,
              'AG': row[10] || 0,
              'RB': row[11] || 0,
              'SC': row[12] || 0,
              'HS': row[13] || 0,
              'RT': row[14] || 0,
              'PH': row[15] || 0,
              'PS': row[16] || 0,
              'EX': row[17] || 0,
              'LD': row[18] || 0,
              'PO': row[19] || 0,
              'MO': row[20] || 0,
              'OV': row[21] || 0,
              'Age': row[24] || 0,  // Column Y
              'Z': row[ROSTER_GOALIE_Z_COLUMN_INDEX] || 0,  // Column Z — roster flag
              'SP': row[23] || 0,  // Column X for SP
              'Salary': row[26] || 0  // Column AA for Salary
            };
          }).filter(Boolean);
          
          devLog('Excel fallback loaded - Players:', playersData.length, 'Goalies:', goaliesData.length);
        } catch (excelError) {
          console.error('Excel fallback failed:', excelError);
          playersData = [];
          goaliesData = [];
        }
      }

      // Format players data
      const formattedData = playersData.map((row, idx) => {
        const positions = [];
        if (row['C'] && row['C'].trim() === 'X') positions.push('C');
        if (row['L'] && row['L'].trim() === 'X') positions.push('L');
        if (row['R'] && row['R'].trim() === 'X') positions.push('R');
        if (row['D'] && row['D'].trim() === 'X') positions.push('D');
        
        let salaryValue = row['Salary'] || row['SP'] || 0;
        if (typeof salaryValue === "string") {
          // Remove $ and , symbols, then parse
          salaryValue = parseFloat(salaryValue.replace(/[$,]/g, '')) || 0;
        }

        const { team, isFreeAgent, contractAf } = resolveRosterTeamFields(row['Team Name'], row['AF']);
        
        const player = {
          id: idx,
          name: row['Player Name'] || "Unknown",
          positions,
          positionDisplay: positions.map(code => codeToDisplay(code, positionMap)).join(', '),
          team,
          isFreeAgent,
          contractAf,
          salary: salaryValue,
          age: row['Age'] || 0
        };
        
        // Map rating columns - use the exact column names from the data
        const ratingMap = {
          'CON': 'CON',
          'CK': 'CK', 
          'FG': 'FG',
          'DI': 'DI',
          'SK': 'SK',
          'ST': 'ST',
          'EN': 'EN',
          'DU': 'DU',
          'PH': 'PH',
          'FO': 'FO',
          'PA': 'PA',
          'SC': 'SC',
          'DF': 'DF',
          'PS': 'PS',
          'EX': 'EX',
          'LD': 'LD',
          'PO': 'PO',
          'MO': 'MO',
          'OV': 'OV'
        };
        
        rateColumns.forEach((rate) => {
          let value = row[ratingMap[rate] || rate] || 0;
          if (typeof value === "string") value = parseFloat(value) || 0;
          player[rate] = Math.round(value);
        });
        
        return player;
      }).filter(player => player.name && player.name !== "Unknown");

      const playersWithFaStatus = applyFaStatusToPlayers(formattedData, skaterMap);

      // Format goalies data
      const formattedGoalieData = goaliesData.map((row, idx) => {
        let salaryValue = row['Salary'] || row['SP'] || 0;
        if (typeof salaryValue === "string") {
          // Remove $ and , symbols, then parse
          salaryValue = parseFloat(salaryValue.replace(/[$,]/g, '')) || 0;
        }
        
        // Debug: Log salary values for first few goalies or Bobrovsky specifically
        if (idx < 5 || (row['Goalie Name'] && row['Goalie Name'].toLowerCase().includes('bobrovsky'))) {
          devLog(`Goalie ${idx} (${row['Goalie Name']}): salary = ${salaryValue}, raw SP = ${row['SP']}, raw Salary = ${row['Salary']}, typeof SP = ${typeof row['SP']}, typeof Salary = ${typeof row['Salary']}`);
          devLog(`  - Salary truthy: ${!!row['Salary']}, SP truthy: ${!!row['SP']}`);
        }
        
        const { team, isFreeAgent, contractZ } = resolveGoalieRosterTeamFields(row['Team Name'], row['Z']);
        
        const goalie = {
          id: idx + 1000,
          name: row['Goalie Name'] || "Unknown",
          positions: ['G'],
          positionDisplay: 'Goalie',
          team,
          isFreeAgent,
          contractZ,
          salary: salaryValue,
          age: row['Age'] || 0
        };
        
        // Map goalie rating columns - use exact column names from data
        const goalieRatingMap = {
          'CON': 'CON',
          'SK': 'SK',
          'DU': 'DU', 
          'EN': 'EN',
          'SZ': 'SZ',
          'AG': 'AG',
          'RB': 'RB',
          'SC': 'SC',
          'HS': 'HS',
          'RT': 'RT',
          'PH': 'PH',
          'PS': 'PS',
          'EX': 'EX',
          'LD': 'LD',
          'PO': 'PO',
          'MO': 'MO',
          'OV': 'OV'
        };
        
        goalieRateColumns.forEach((rateObj) => {
          const rateName = typeof rateObj === 'string' ? rateObj : rateObj.name;
          let value = row[goalieRatingMap[rateName] || rateName] || 0;
          if (typeof value === "string") value = parseFloat(value) || 0;
          goalie[rateName] = Math.round(value);
        });
        
        return goalie;
      }).filter(goalie => goalie.name && goalie.name !== "Unknown");

      const goaliesWithFaStatus = formattedGoalieData.map((goalie) =>
        applyFaStatusToPlayer(goalie, goalieMap)
      );

      devLog('Formatted players count:', playersWithFaStatus.length);
      devLog('Formatted goalies count:', goaliesWithFaStatus.length);
      
      setPlayers(playersWithFaStatus);
      setGoalies(goaliesWithFaStatus);
      setCachedRoster(playersWithFaStatus, goaliesWithFaStatus, {});
      applyTeamListFromRoster(playersWithFaStatus, goaliesWithFaStatus);
      
      setForwards([
        { LW: null, C: null, RW: null },
        { LW: null, C: null, RW: null },
        { LW: null, C: null, RW: null },
        { LW: null, C: null, RW: null },
        { LW: null, C: null, RW: null }
      ]);
      setDefensemen([
        { D1: null, D2: null },
        { D1: null, D2: null },
        { D1: null, D2: null },
        { D1: null, D2: null }
      ]);
      setError(null);
      setDataUpdatedAt(new Date());
      devLog('Data loading completed successfully');
    } catch (err) {
      console.error('Error in loadData:', err);
      setError("Error loading data: " + err.message);
    }
    } finally {
      setIsLoadingRoster(false);
    }
  };

  useEffect(() => {
    if (players.length === 0) {
      loadData();
    }
    // loadData is intentionally run only when the roster is empty on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players.length]);

  useEffect(() => {
    localStorage.setItem(
      WISHLIST_STORAGE_KEY,
      JSON.stringify({
        players: wishlistPlayers,
        goalies: wishlistGoalies,
        salaries: wishlistSalaries,
      })
    );
  }, [wishlistPlayers, wishlistGoalies, wishlistSalaries]);

  useEffect(() => {
    savePersistedFilters({
      position,
      team,
      searchTerm,
      sortColumn,
      sortDirection,
      playerTypeFilter,
      playerViewMode,
      goalieViewMode,
    });
  }, [position, team, searchTerm, sortColumn, sortDirection, playerTypeFilter, playerViewMode, goalieViewMode]);

  const undoLineupChangeRef = useRef(() => {});
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (undoStackRef.current.length === 0) return;
        e.preventDefault();
        undoLineupChangeRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (players.length === 0 && goalies.length === 0) return;

    setWishlistPlayers((prev) =>
      prev
        .map((wp) => players.find((p) => p.id === wp.id || p.name === wp.name) || wp)
        .filter((wp) => players.some((p) => p.name === wp.name))
    );
    setWishlistGoalies((prev) =>
      prev
        .map((wg) => goalies.find((g) => g.id === wg.id || g.name === wg.name) || wg)
        .filter((wg) => goalies.some((g) => g.name === wg.name))
    );
  }, [players, goalies]);

  useEffect(() => {
    // Remove all theme classes first
    document.body.classList.remove('light-mode', 'dark-mode', 'gray-mode', 'black-mode');
    
    // Add the appropriate theme class
    if (theme !== 'light') {
      document.body.classList.add(`${theme}-mode`);
    }
  }, [theme]);


  // Helper functions
  function getCurrentAssignment(player) {
    for (let lineIndex = 0; lineIndex < forwards.length; lineIndex++) {
      const line = forwards[lineIndex];
      for (let pos in line) {
        if (line[pos] && line[pos].id === player.id) {
          return `forwards-${lineIndex}-${pos}`;
        }
      }
    }
    for (let lineIndex = 0; lineIndex < defensemen.length; lineIndex++) {
      const line = defensemen[lineIndex];
      for (let pos in line) {
        if (line[pos] && line[pos].id === player.id) {
          return `defensemen-${lineIndex}-${pos}`;
        }
      }
    }
    return "";
  }

  const isAssigned = (player) => playerAssignmentMap.has(player.id);
  const isGoalieAssigned = (goalie) => goalieAssignmentMap.has(goalie.id);

  const isPositionAssigned = (type, line, pos) => {
    if (type === 'forwards') {
      return forwards[line][pos] !== null;
    } else if (type === 'defensemen') {
      return defensemen[line][pos] !== null;
    }
    return false;
  };

  const getAssignmentOptions = (player) => {
    const options = [];
    const currentAssignment = getCurrentAssignment(player);
    if (!currentAssignment) {
      // Check if player is a forward
      const isForward = player.positions.some(pos => ['L', 'C', 'R'].includes(pos));
      
      if (isForward) {
        // For regular forward lines (0-3), only allow natural positions
        if (player.positions.includes('L')) {
          for (let line = 0; line < 4; line++) {
            if (!isPositionAssigned('forwards', line, 'LW')) {
              options.push({
                value: `forwards-${line}-LW`,
                label: `Forwards Line ${line + 1} - LW`
              });
            }
          }
        }
        if (player.positions.includes('C')) {
          for (let line = 0; line < 4; line++) {
            if (!isPositionAssigned('forwards', line, 'C')) {
              options.push({
                value: `forwards-${line}-C`,
                label: `Forwards Line ${line + 1} - C`
              });
            }
          }
        }
        if (player.positions.includes('R')) {
          for (let line = 0; line < 4; line++) {
            if (!isPositionAssigned('forwards', line, 'RW')) {
              options.push({
                value: `forwards-${line}-RW`,
                label: `Forwards Line ${line + 1} - RW`
              });
            }
          }
        }
        
        // For Extra FW line (line 4), allow any forward to any position - placed at bottom
        if (!isPositionAssigned('forwards', 4, 'LW')) {
          options.push({
            value: `forwards-4-LW`,
            label: `Extra FW1`
          });
        }
        if (!isPositionAssigned('forwards', 4, 'C')) {
          options.push({
            value: `forwards-4-C`,
            label: `Extra FW2`
          });
        }
        if (!isPositionAssigned('forwards', 4, 'RW')) {
          options.push({
            value: `forwards-4-RW`,
            label: `Extra FW3`
          });
        }
      }
      if (player.positions.includes('D')) {
        for (let line = 0; line < 4; line++) {
          if (!isPositionAssigned('defensemen', line, 'D1')) {
            const lineName = line === 3 ? "Extra D" : `Defensemen Line ${line + 1}`;
            options.push({
              value: `defensemen-${line}-D1`,
              label: `${lineName} - D1`
            });
          }
          if (!isPositionAssigned('defensemen', line, 'D2')) {
            const lineName = line === 3 ? "Extra D" : `Defensemen Line ${line + 1}`;
            options.push({
              value: `defensemen-${line}-D2`,
              label: `${lineName} - D2`
            });
          }
        }
      }
    } else {
      options.push({
        value: currentAssignment,
        label: getAssignmentLabel(currentAssignment)
      });
    }
    return options;
  };

  // Goalie helper functions
  const getCurrentGoalieAssignment = (goalie) => {
    for (let i = 0; i < lineupGoalies.length; i++) {
      if (lineupGoalies[i] && lineupGoalies[i].id === goalie.id) {
        return `goalie${i + 1}`;
      }
    }
    return null;
  };


  const getGoalieAssignmentOptions = (goalie) => {
    const options = [];
    const currentAssignment = getCurrentGoalieAssignment(goalie);
    if (!currentAssignment) {
      for (let i = 0; i < 3; i++) {
        if (!lineupGoalies[i]) {
          options.push({
            value: `goalie${i + 1}`,
            label: `Goalie ${i + 1}`
          });
        }
      }
    } else {
      options.push({
        value: currentAssignment,
        label: `Goalie ${currentAssignment.replace('goalie', '')}`
      });
    }
    return options;
  };

  const handleHeaderClick = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(col);
      setSortDirection("desc");
    }
  };

  const handleGoalieHeaderClick = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(col);
      setSortDirection("desc");
    }
  };

  const handlePositionFilter = (e) => {
    const next = e.target.value;
    setPosition(next);
    if (next === "Goalie" && sortColumn === "playerType") {
      setSortColumn("OV");
    }
  };
  const handleTeamFilter = (e) => setTeam(e.target.value);
  const handleSortDirectionChange = (e) => setSortDirection(e.target.value);
  const handleSearch = (e) => setSearchTerm(e.target.value);


  const handleLoadLineup = (savedLineup) => {
    // Store the currently loaded lineup for potential updates
    setCurrentLoadedLineup(savedLineup);
    
    // Helper function to reconstruct full player objects from saved minimal data
    const reconstructPlayer = (savedPlayer) => {
      if (!savedPlayer) return null;
      
      // Always try to find by name first (most reliable for current data)
      let fullPlayer = null;
      if (savedPlayer.name) {
        fullPlayer = players.find(p => p.name === savedPlayer.name);
        if (fullPlayer) {
          return fullPlayer;
        } else {
          // Try partial name match
          fullPlayer = players.find(p => p.name && p.name.toLowerCase().includes(savedPlayer.name.toLowerCase()));
          if (fullPlayer) {
            return fullPlayer;
          }
        }
      }
      
      // Fall back to ID lookup only if name lookup fails
      fullPlayer = players.find(p => p.id === savedPlayer.id);
      if (fullPlayer) {
        return fullPlayer;
      }
      
      // If not found, ensure saved player has all required rating properties
      const reconstructed = { ...savedPlayer };
      rateColumns.forEach(rate => {
        if (!reconstructed[rate]) {
          reconstructed[rate] = 0;
        }
      });
      
      return reconstructed;
    };

    // Helper function to reconstruct full goalie objects
    const reconstructGoalie = (savedGoalie) => {
      if (!savedGoalie) return null;
      
      // Always try to find by name first (most reliable for current data)
      let fullGoalie = null;
      if (savedGoalie.name) {
        fullGoalie = goalies.find(g => g.name === savedGoalie.name);
        if (fullGoalie) {
          devLog(`Reconstructed goalie ${savedGoalie.name}: found current data with salary ${fullGoalie.salary}`);
          return fullGoalie;
        } else {
          // Try partial name match
          fullGoalie = goalies.find(g => g.name && g.name.toLowerCase().includes(savedGoalie.name.toLowerCase()));
          if (fullGoalie) {
            devLog(`Reconstructed goalie ${savedGoalie.name}: found partial match with salary ${fullGoalie.salary}`);
            return fullGoalie;
          }
        }
      }
      
      // Fall back to ID lookup only if name lookup fails
      fullGoalie = goalies.find(g => g.id === savedGoalie.id);
      if (fullGoalie) {
        devLog(`Reconstructed goalie ${savedGoalie.name}: found by ID with salary ${fullGoalie.salary}`);
        return fullGoalie;
      }
      
      // If not found, ensure saved goalie has all required rating properties
      devLog(`Reconstructed goalie ${savedGoalie.name}: using saved data with salary ${savedGoalie.salary || 'undefined'}`);
      const reconstructed = { ...savedGoalie };
      goalieRateColumns.forEach(rateObj => {
        const rateName = typeof rateObj === 'string' ? rateObj : rateObj.name;
        if (!reconstructed[rateName]) {
          reconstructed[rateName] = 0;
        }
      });
      
      return reconstructed;
    };
    
    // Restore lineup state from saved data, reconstructing full player objects
    const reconstructedForwards = (savedLineup.forwards || [
      { LW: null, C: null, RW: null },
      { LW: null, C: null, RW: null },
      { LW: null, C: null, RW: null },
      { LW: null, C: null, RW: null },
      { LW: null, C: null, RW: null }
    ]).map(line => ({
      LW: reconstructPlayer(line.LW),
      C: reconstructPlayer(line.C),
      RW: reconstructPlayer(line.RW)
    }));
    
    const reconstructedDefensemen = (savedLineup.defensemen || [
      { D1: null, D2: null },
      { D1: null, D2: null },
      { D1: null, D2: null },
      { D1: null, D2: null }
    ]).map(line => ({
      D1: reconstructPlayer(line.D1),
      D2: reconstructPlayer(line.D2)
    }));
    
    const reconstructedGoalies = (savedLineup.goalies || [null, null, null]).map(reconstructGoalie);
    
    // Rebuild salaries state: first use saved custom salaries, then fill in missing ones from player data
    const updatedSalaries = { ...savedLineup.salaries || {} };
    
    // Update salaries with current player data, prioritizing current data over saved values
    [...reconstructedForwards.flatMap(line => Object.values(line)), 
     ...reconstructedDefensemen.flatMap(line => Object.values(line)),
     ...reconstructedGoalies].forEach(player => {
      if (player && player.id) {
        // Always use current player salary if available, otherwise keep saved value or default to 0
        if (player.salary !== undefined && player.salary !== null) {
          updatedSalaries[player.id] = player.salary.toString();
        } else if (!updatedSalaries[player.id]) {
          updatedSalaries[player.id] = "0";
        }
      }
    });
    
    pushUndoSnapshot();
    setForwards(reconstructedForwards);
    setDefensemen(reconstructedDefensemen);
    setLineupGoalies(reconstructedGoalies);
    setSalaries(updatedSalaries);
    
    if (savedLineup.salaryCap) {
      setSalaryCap(savedLineup.salaryCap);
    }
    
    if (savedLineup.retainedSalary) {
      setRetainedSalary(savedLineup.retainedSalary);
    }
  };

  const handleQuickSave = async () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }

    // If there's a loaded lineup, update it directly
    if (currentLoadedLineup) {
      setQuickSaveLoading(true);
      try {
        const lineupData = lineupService.formatLineupForSave(
          currentLoadedLineup.name,
          forwards,
          defensemen,
          lineupGoalies,
          salaries,
          salaryCap,
          retainedSalary
        );
        
        await lineupService.updateLineup(currentLoadedLineup.id, lineupData);
        // Update the current loaded lineup data to reflect the save
        setCurrentLoadedLineup({ ...currentLoadedLineup, ...lineupData });
      } catch (error) {
        console.error('Error updating lineup:', error);
        alert('Failed to save lineup');
      } finally {
        setQuickSaveLoading(false);
      }
    } else {
      // No loaded lineup, prompt for name
      setQuickSavePromptOpen(true);
    }
  };

  const handleQuickSaveWithName = async () => {
    if (!quickSaveLineupName.trim()) {
      alert('Please enter a lineup name');
      return;
    }

    setQuickSaveLoading(true);
    try {
      const lineupData = lineupService.formatLineupForSave(
        quickSaveLineupName.trim(),
        forwards,
        defensemen,
        lineupGoalies,
        salaries,
        salaryCap,
        retainedSalary
      );
      
      const lineupId = await lineupService.saveLineup(user.uid, lineupData);
      // Set this as the current loaded lineup
      setCurrentLoadedLineup({ ...lineupData, id: lineupId });
      setQuickSaveLineupName('');
      setQuickSavePromptOpen(false);
    } catch (error) {
      console.error('Error saving lineup:', error);
      alert('Failed to save lineup');
    } finally {
      setQuickSaveLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, player, fromLocation) => {
    setDraggedPlayer(player);
    setDraggedFrom(fromLocation);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, toLocation) => {
    e.preventDefault();
    
    if (!draggedPlayer || !draggedFrom) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    const isValid = isCompatiblePosition(draggedPlayer, toLocation);
    e.dataTransfer.dropEffect = isValid ? 'move' : 'none';
    
    // Add visual feedback classes
    const targetElement = e.currentTarget;
    targetElement.classList.remove('valid-drop', 'invalid-drop');
    
    if (isValid) {
      targetElement.classList.add('valid-drop');
    } else {
      targetElement.classList.add('invalid-drop');
    }
  };

  const handleDrop = (e, toLocation) => {
    e.preventDefault();
    
    // Clean up visual feedback
    e.currentTarget.classList.remove('valid-drop', 'invalid-drop');
    
    if (!draggedPlayer || !draggedFrom) return;

    // Don't allow drop on same position
    if (draggedFrom.type === toLocation.type && 
        draggedFrom.lineIndex === toLocation.lineIndex && 
        draggedFrom.position === toLocation.position) {
      setDraggedPlayer(null);
      setDraggedFrom(null);
      return;
    }

    // Check if target position is compatible
    if (!isCompatiblePosition(draggedPlayer, toLocation)) {
      setDraggedPlayer(null);
      setDraggedFrom(null);
      return;
    }

    pushUndoSnapshot();

    // Get the player at the target position (if any)
    const targetPlayer = getPlayerAtPosition(toLocation);

    // Remove player from original position
    removePlayerFromPosition(draggedFrom);

    // Place dragged player at target position
    setPlayerAtPosition(toLocation, draggedPlayer);

    // If there was a player at target, place them at the original position
    if (targetPlayer) {
      setPlayerAtPosition(draggedFrom, targetPlayer);
    }

    setDraggedPlayer(null);
    setDraggedFrom(null);
  };

  const handleDragEnd = (e) => {
    // Clean up all visual feedback when drag ends
    document.querySelectorAll('.valid-drop, .invalid-drop').forEach(element => {
      element.classList.remove('valid-drop', 'invalid-drop');
    });
    
    setDraggedPlayer(null);
    setDraggedFrom(null);
  };

  const isCompatiblePosition = (player, toLocation) => {
    // Goalies can only go to goalie positions
    if (player.rates !== undefined) {
      return toLocation.type === 'goalie';
    }

    // Regular players can't go to goalie positions
    if (toLocation.type === 'goalie') {
      return false;
    }

    // For Extra FW line (line 4), only allow forwards
    if (toLocation.type === 'forwards' && toLocation.lineIndex === 4) {
      return player.positions && player.positions.some(pos => ['L', 'C', 'R'].includes(pos));
    }

    // For defense lines, only allow defensemen
    if (toLocation.type === 'defensemen') {
      return player.positions && player.positions.includes('D');
    }

    // For forward lines, only allow forwards
    if (toLocation.type === 'forwards') {
      return player.positions && player.positions.some(pos => ['L', 'C', 'R'].includes(pos));
    }

    return true;
  };

  const getPlayerAtPosition = (location) => {
    if (location.type === 'forwards') {
      return forwards[location.lineIndex][location.position];
    } else if (location.type === 'defensemen') {
      return defensemen[location.lineIndex][location.position];
    } else if (location.type === 'goalie') {
      return lineupGoalies[location.index];
    }
    return null;
  };

  const removePlayerFromPosition = (location) => {
    if (location.type === 'forwards') {
      setForwards(prev => {
        const newForwards = [...prev];
        newForwards[location.lineIndex][location.position] = null;
        return newForwards;
      });
    } else if (location.type === 'defensemen') {
      setDefensemen(prev => {
        const newDefensemen = [...prev];
        newDefensemen[location.lineIndex][location.position] = null;
        return newDefensemen;
      });
    } else if (location.type === 'goalie') {
      setLineupGoalies(prev => {
        const newGoalies = [...prev];
        newGoalies[location.index] = null;
        return newGoalies;
      });
    }
  };

  const setPlayerAtPosition = (location, player) => {
    if (location.type === 'forwards') {
      setForwards(prev => {
        const newForwards = [...prev];
        newForwards[location.lineIndex][location.position] = player;
        return newForwards;
      });
    } else if (location.type === 'defensemen') {
      setDefensemen(prev => {
        const newDefensemen = [...prev];
        newDefensemen[location.lineIndex][location.position] = player;
        return newDefensemen;
      });
    } else if (location.type === 'goalie') {
      setLineupGoalies(prev => {
        const newGoalies = [...prev];
        newGoalies[location.index] = player;
        return newGoalies;
      });
    }
  };

  const pushUndoSnapshot = useCallback(() => {
    undoStackRef.current.push({
      forwards: forwards.map((line) => ({ ...line })),
      defensemen: defensemen.map((line) => ({ ...line })),
      lineupGoalies: [...lineupGoalies],
      salaries: { ...salaries },
    });
    if (undoStackRef.current.length > 25) {
      undoStackRef.current.shift();
    }
    setUndoCount(undoStackRef.current.length);
  }, [forwards, defensemen, lineupGoalies, salaries]);

  const undoLineupChange = () => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    setForwards(previous.forwards);
    setDefensemen(previous.defensemen);
    setLineupGoalies(previous.lineupGoalies);
    setSalaries(previous.salaries);
    setUndoCount(undoStackRef.current.length);
  };
  undoLineupChangeRef.current = undoLineupChange;

  const handleAssignment = (player, value) => {
    // Keep currentLoadedLineup so users can save over it, but they'll see unsaved changes
    // setCurrentLoadedLineup(null); // Commented out to allow quick save over existing lineup
    pushUndoSnapshot();

    if (value === "unassign") {
      setForwards(prev =>
        prev.map(line =>
          Object.fromEntries(
            Object.entries(line).map(([pos, p]) =>
              p && p.id === player.id ? [pos, null] : [pos, p]
            )
          )
        )
      );
      setDefensemen(prev =>
        prev.map(line =>
          Object.fromEntries(
            Object.entries(line).map(([pos, p]) =>
              p && p.id === player.id ? [pos, null] : [pos, p]
            )
          )
        )
      );
      setLineupGoalies(prev =>
        prev.map(g => (g && g.id === player.id ? null : g))
      );
      return;
    } else if (value.startsWith("goalie")) {
      const goalieIndex = parseInt(value.replace("goalie", "")) - 1;
      setLineupGoalies(prev => {
        const newGoalies = [...prev];
        newGoalies[goalieIndex] = player;
        return newGoalies;
      });
      // Always update the goalie's salary to current data (fixes issue after loading saved lineups)
      devLog(`Setting salary for goalie ${player.name}: ${player.salary} (previous: ${salaries[player.id] || 'none'})`);
      setSalaries(prev => ({
        ...prev,
        [player.id]: String(player.salary || 0)
      }));
    } else {
      if (value.startsWith("forwards")) {
        const [, lineIdx, pos] = value.split("-");
        setForwards(prev => {
          const newLines = prev.map(line => ({ ...line }));
          newLines[parseInt(lineIdx)][pos] = player;
          return newLines;
        });
      } else if (value.startsWith("defensemen")) {
        const [, lineIdx, pos] = value.split("-");
        setDefensemen(prev => {
          const newLines = prev.map(line => ({ ...line }));
          newLines[parseInt(lineIdx)][pos] = player;
          return newLines;
        });
      }
      // Always update the player's salary to current data (fixes issue after loading saved lineups)
      devLog(`Setting salary for player ${player.name}: ${player.salary} (previous: ${salaries[player.id] || 'none'})`);
      setSalaries(prev => ({
        ...prev,
        [player.id]: String(player.salary || 0)
      }));
    }
  };

  const baseSortOptions = ["name", "team", "age", "salary"];
  const sortOptions = position === "Goalie"
    ? baseSortOptions.concat(goalieRateColumns.map(c => c.name))
    : baseSortOptions.concat(["playerType"], rateColumns);

  // Calculate total salary for display
  const assignedPlayers = [
    ...forwards.flatMap(line => Object.values(line).filter(Boolean)),
    ...defensemen.flatMap(line => Object.values(line).filter(Boolean)),
    ...lineupGoalies.filter(Boolean)
  ];
  const totalSalary = assignedPlayers.reduce((sum, player) => {
    const salary = parseInt(salaries[player.id], 10) || 0;
    return sum + salary;
  }, 0) + retainedSalary;

  const handleUserTeamChange = (e) => {
    const team = e.target.value;
    setSelectedUserTeam(team);
    if (user?.uid) {
      saveUserTeam(user.uid, team);
    }
  };

  const handleLoadTeamRoster = () => {
    if (!selectedUserTeam) {
      alert("Select a team first.");
      return;
    }

    const rosteredCount =
      players.filter((p) => isRosteredOnTeam(p, selectedUserTeam)).length +
      goalies.filter((g) => isRosteredGoalieOnTeam(g, selectedUserTeam)).length;

    if (rosteredCount === 0) {
      alert(`No rostered players found for ${selectedUserTeam} (skaters need AF >= 1, goalies need Z >= 1).`);
      return;
    }

    if (
      assignedPlayers.length > 0 &&
      !window.confirm(`Replace your current lineup with the ${selectedUserTeam} roster?`)
    ) {
      return;
    }

    pushUndoSnapshot();
    const result = buildLineupFromTeam(players, goalies, selectedUserTeam);
    setForwards(result.forwards);
    setDefensemen(result.defensemen);
    setLineupGoalies(result.lineupGoalies);
    setSalaries((prev) => ({ ...prev, ...result.salaries }));
    setCurrentLoadedLineup(null);
  };

  // Calculate position breakdown for roster count
  const getPositionBreakdown = () => {
    const positionCounts = {};
    
    assignedPlayers.forEach(player => {
      if (goalies.some(g => g.id === player.id)) {
        // This is a goalie
        positionCounts['G'] = (positionCounts['G'] || 0) + 1;
      } else if (player.positions) {
        // This is a skater - create position combination string
        const sortedPositions = [...player.positions].sort();
        const positionKey = sortedPositions.map(code => codeToDisplay(code, positionMap)).join('/');
        positionCounts[positionKey] = (positionCounts[positionKey] || 0) + 1;
      }
    });
    
    return positionCounts;
  };

  // Position search functions
  const getPositionKey = (type, lineIndex, position, index) => {
    if (type === 'goalie') {
      return `goalie-${index}`;
    }
    return `${type}-${lineIndex}-${position}`;
  };

  const getEligiblePlayersForPosition = (type, lineIndex, position) => {
    if (type === 'goalie') {
      return goalies.filter(player => !isGoalieAssigned(player));
    }
    
    const unassignedPlayers = players.filter(player => !isAssigned(player));
    
    if (type === 'forwards') {
      const forwardPlayers = unassignedPlayers.filter(player => 
        player.positions && player.positions.some(pos => ['L', 'C', 'R'].includes(pos))
      );
      
      // For Extra FW line (lineIndex 4), allow any forward
      if (lineIndex === 4) {
        return forwardPlayers;
      }
      
      // For regular forward lines, filter by position
      if (position === 'LW') {
        return forwardPlayers.filter(player => player.positions.includes('L'));
      } else if (position === 'C') {
        return forwardPlayers.filter(player => player.positions.includes('C'));
      } else if (position === 'RW') {
        return forwardPlayers.filter(player => player.positions.includes('R'));
      }
    } else if (type === 'defensemen') {
      return unassignedPlayers.filter(player => 
        player.positions && player.positions.includes('D')
      );
    }
    
    return [];
  };

  const handlePositionSearch = (positionKey, searchTerm, type, lineIndex, position) => {
    setPositionSearches(prev => ({ ...prev, [positionKey]: searchTerm }));
    
    if (!searchTerm.trim()) {
      setPositionSearchResults(prev => ({ ...prev, [positionKey]: [] }));
      return;
    }
    
    const eligiblePlayers = getEligiblePlayersForPosition(type, lineIndex, position);
    const filteredResults = eligiblePlayers
      .filter(player => 
        player.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 5); // Limit to 5 results
    
    setPositionSearchResults(prev => ({ ...prev, [positionKey]: filteredResults }));
  };

  const handlePlayerSelect = (player, type, lineIndex, position, index) => {
    const positionKey = getPositionKey(type, lineIndex, position, index);
    pushUndoSnapshot();

    // Assign the player
    if (type === 'goalie') {
      setLineupGoalies(prev => {
        const newGoalies = [...prev];
        newGoalies[index] = player;
        return newGoalies;
      });
      // Always update the goalie's salary to current data (fixes issue after loading saved lineups)
      setSalaries(prev => ({
        ...prev,
        [player.id]: String(player.salary || 0)
      }));
    } else if (type === 'forwards') {
      setForwards(prev => {
        const newLines = prev.map(line => ({ ...line }));
        newLines[lineIndex][position] = player;
        return newLines;
      });
      // Always update the player's salary to current data
      setSalaries(prev => ({
        ...prev,
        [player.id]: String(player.salary || 0)
      }));
    } else if (type === 'defensemen') {
      setDefensemen(prev => {
        const newLines = prev.map(line => ({ ...line }));
        newLines[lineIndex][position] = player;
        return newLines;
      });
      // Always update the player's salary to current data
      setSalaries(prev => ({
        ...prev,
        [player.id]: String(player.salary || 0)
      }));
    }
    
    // Clear search
    setPositionSearches(prev => ({ ...prev, [positionKey]: '' }));
    setPositionSearchResults(prev => ({ ...prev, [positionKey]: [] }));
  };

  // Context menu functions
  const handlePlayerClick = useCallback((e, player) => {
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      player: player
    });
  }, []);

  const hideContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, player: null });
  };

  // Lineup player click handler - toggles between name and rates display
  const handleLineupPlayerClick = (e, player) => {
    e.stopPropagation();
    const playerId = player.id;
    setExpandedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const renderPlayerNameBadges = (player, size = 'normal', showPlayerType = true) => (
    <PlayerNameBadges player={player} size={size} showPlayerType={showPlayerType} />
  );

  const addToWishlist = (player, isGoalie = false) => {
    if (isGoalie) {
      setWishlistGoalies(prev => {
        if (prev.some(p => p.id === player.id)) return prev;
        return [...prev, player];
      });
    } else {
      setWishlistPlayers(prev => {
        if (prev.some(p => p.id === player.id)) return prev;
        return [...prev, player];
      });
    }
  };

  const removeFromWishlist = (player, isGoalie = false) => {
    if (isGoalie) {
      setWishlistGoalies(prev => prev.filter(p => p.id !== player.id));
    } else {
      setWishlistPlayers(prev => prev.filter(p => p.id !== player.id));
    }
    // Also remove from wishlist salaries
    setWishlistSalaries(prev => {
      const updated = { ...prev };
      delete updated[player.id];
      return updated;
    });
  };

  const toggleWishlist = (player, isGoalie = false) => {
    if (isInWishlist(player, isGoalie)) {
      removeFromWishlist(player, isGoalie);
    } else {
      addToWishlist(player, isGoalie);
    }
  };

  const isInWishlist = (player, isGoalie = false) => {
    if (isGoalie) {
      return wishlistGoalies.some(p => p.id === player.id);
    } else {
      return wishlistPlayers.some(p => p.id === player.id);
    }
  };

  const renderPlayerCard = useCallback(
    (player) => (
      <PlayerCard
        key={player.id}
        player={player}
        rateColumns={rateColumns}
        rateMin={playerRatingRange.min}
        rateMax={playerRatingRange.max}
        assignment={playerAssignmentMap.get(player.id) || ''}
        onPlayerClick={handlePlayerClick}
        onAddToLineup={(player, e) => handlePlayerClick(e, player)}
      />
    ),
    [playerRatingRange, playerAssignmentMap, handlePlayerClick]
  );

  const renderGoalieCard = useCallback(
    (goalie) => (
      <GoalieCard
        key={goalie.id}
        goalie={goalie}
        goalieRateColumns={goalieRateColumns}
        rateMin={goalieRatingRange.min}
        rateMax={goalieRatingRange.max}
        assignment={goalieAssignmentMap.get(goalie.id) || ''}
        onPlayerClick={handlePlayerClick}
        onAddToLineup={(player, e) => handlePlayerClick(e, player)}
      />
    ),
    [goalieRatingRange, goalieAssignmentMap, handlePlayerClick]
  );

  // Helper function to render player content (name/salary or ratings)
  const renderPlayerContent = (player) => {
    const isExpanded = expandedPlayers.has(player.id);
    const isGoalie = goalies.some(g => g.id === player.id);
    
    if (!isExpanded) {
      // Show normal name and salary
      return (
        <div className="player-content-default">
          <div className="player-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{player.name}</span>
            {renderPlayerNameBadges(player, 'small', !isGoalie)}
          </div>
          {player.age && (
            <div className="player-age" style={{ fontSize: '0.75rem', color: '#888' }}>
              Age: {player.age}
            </div>
          )}
          <div className="player-salary">
            ${salaries[player.id] ? Number(salaries[player.id].replace(/[^0-9]/g, '')).toLocaleString('en-US') : '0'}
          </div>
        </div>
      );
    } else {
      // Show ratings in horizontal layout
      if (isGoalie) {
        // Separate OV from other goalie ratings for different color scales
        const regularGoalieColumns = goalieRateColumns.filter(col => col.name !== 'OV');
        const ovColumn = goalieRateColumns.find(col => col.name === 'OV');
        
        // Color scale for regular goalie ratings (excluding OV)
        const regularRatingValues = regularGoalieColumns.map(col => player[col.name] || 0);
        const regularMin = Math.min(...regularRatingValues);
        const regularMax = Math.max(...regularRatingValues);
        
        // Color scale for OV based on all goalies' OV values
        const allGoalieOVValues = goalies.map(g => g.rates?.OV || 0).filter(v => v > 0);
        const ovMin = allGoalieOVValues.length > 0 ? Math.min(...allGoalieOVValues) : 0;
        const ovMax = allGoalieOVValues.length > 0 ? Math.max(...allGoalieOVValues) : 100;
        
        return (
          <div className="player-content-expanded" style={{ width: '100%', height: '100%' }}>
            <div className="player-ratings-horizontal" style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'space-between', 
              alignItems: 'stretch',
              gap: '2px',
              width: '100%',
              height: '100%',
              padding: '4px',
              boxSizing: 'border-box'
            }}>
              {/* Render regular goalie ratings */}
              {regularGoalieColumns.map(col => {
                const value = player[col.name] || 0;
                const backgroundColor = getRatingColor(value, regularMin, regularMax);
                
                return (
                  <div 
                    key={col.name}
                    className="rating-item-horizontal"
                    style={{ 
                      backgroundColor, 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '9px',
                      minHeight: '28px',
                      flex: '1 1 calc((100% - 26px) / 14)',
                      maxWidth: 'calc((100% - 26px) / 14)',
                      boxSizing: 'border-box',
                      color: 'black'
                    }}
                  >
                    <span className="rating-label" style={{ fontSize: '7px', fontWeight: '600', marginBottom: '1px' }}>{col.name}</span>
                    <span className="rating-value" style={{ fontSize: '9px', fontWeight: '700' }}>{value}</span>
                  </div>
                );
              })}
              
              {/* Render OV rating with its own color scale */}
              {ovColumn && (
                <div 
                  key={ovColumn.name}
                  className="rating-item-horizontal"
                  style={{ 
                    backgroundColor: getRatingColor(player[ovColumn.name] || 0, ovMin, ovMax), 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px',
                    border: 'none',
                    borderRadius: '3px',
                    fontSize: '9px',
                    minHeight: '28px',
                    flex: '1 1 calc((100% - 26px) / 14)',
                    maxWidth: 'calc((100% - 26px) / 14)',
                    boxSizing: 'border-box',
                    color: 'black'
                  }}
                >
                  <span className="rating-label" style={{ fontSize: '7px', fontWeight: '600', marginBottom: '1px' }}>{ovColumn.name}</span>
                  <span className="rating-value" style={{ fontSize: '9px', fontWeight: '700' }}>{player[ovColumn.name] || 0}</span>
                </div>
              )}
            </div>
          </div>
        );
      } else {
        const ratingValues = rateColumns.map(rate => player[rate] || 0);
        const min = Math.min(...ratingValues);
        const max = Math.max(...ratingValues);
        
        return (
          <div className="player-content-expanded" style={{ width: '100%', height: '100%' }}>
            <div className="player-ratings-horizontal" style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'space-between', 
              alignItems: 'stretch',
              gap: '2px',
              width: '100%',
              height: '100%',
              padding: '4px',
              boxSizing: 'border-box'
            }}>
              {rateColumns.map(rate => {
                const value = player[rate] || 0;
                const backgroundColor = getRatingColor(value, min, max);
                
                return (
                  <div 
                    key={rate}
                    className="rating-item-horizontal"
                    style={{ 
                      backgroundColor, 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '9px',
                      minHeight: '28px',
                      flex: '1 1 calc((100% - 30px) / 16)',
                      maxWidth: 'calc((100% - 30px) / 16)',
                      boxSizing: 'border-box',
                      color: 'black'
                    }}
                  >
                    <span className="rating-label" style={{ fontSize: '7px', fontWeight: '600', marginBottom: '1px' }}>{rate}</span>
                    <span className="rating-value" style={{ fontSize: '9px', fontWeight: '700' }}>{value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
    }
  };

  const handleContextMenuAssignment = (assignmentValue) => {
    if (contextMenu.player) {
      handleAssignment(contextMenu.player, assignmentValue);
      if (wishlistSalaries[contextMenu.player.id]) {
        setSalaries((prev) => ({
          ...prev,
          [contextMenu.player.id]: String(wishlistSalaries[contextMenu.player.id]),
        }));
      }
    }
    hideContextMenu();
  };

  const getLineupAssignmentDisplay = (player, isGoalie) => {
    if (isGoalie) {
      const assignment = goalieAssignmentMap.get(player.id);
      return assignment ? getGoalieAssignmentLabel(assignment) : null;
    }
    const assignment = playerAssignmentMap.get(player.id);
    return assignment ? getAssignmentLabel(assignment) : null;
  };

  const openAssignmentMenu = (player, e) => {
    setContextMenu({
      visible: true,
      x: e?.clientX ?? Math.min(window.innerWidth - 240, Math.max(16, window.innerWidth / 2 - 110)),
      y: e?.clientY ?? 140,
      player,
    });
  };

  // Close context menu when clicking elsewhere
  React.useEffect(() => {
    const handleClick = () => hideContextMenu();
    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible]);

  const showExportNotice = (message) => {
    setExportNotice(message);
    window.setTimeout(() => setExportNotice(''), 2500);
  };

  const getLineupShareText = () =>
    formatLineupText(forwards, defensemen, lineupGoalies, {
      title: selectedUserTeam ? `${selectedUserTeam} Lineup` : 'My Lineup',
    });

  const copyLineup = async () => {
    const ok = await copyTextToClipboard(getLineupShareText());
    showExportNotice(ok ? 'Lineup copied to clipboard' : 'Could not copy lineup');
  };

  const downloadLineupTxt = () => {
    downloadTextFile(getLineupShareText(), 'tugg-lineup.txt');
    showExportNotice('Lineup downloaded as text');
  };

  const downloadLineupPng = () => {
    downloadLineupImage(forwards, defensemen, lineupGoalies, {
      title: selectedUserTeam ? `${selectedUserTeam} Lineup` : 'Tugg Lineup',
    });
    showExportNotice('Lineup image downloaded');
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <div className="app-title-section">
            <h1 className="app-title">Tugg Lineup Builder</h1>
            {currentLoadedLineup && (
              <div className="loaded-lineup-indicator">
                Currently loaded: <strong>{currentLoadedLineup.name}</strong>
              </div>
            )}
          </div>
          
          <div className="header-controls">
            <div className="auth-controls">
              {isAuthenticated ? (
                <>
                  <span className="user-info">Welcome, {user.email}</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => setLineupModalOpen(true)}>
                    Manage Lineups
                  </button>
                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={handleQuickSave}
                    disabled={quickSaveLoading}
                  >
                    {quickSaveLoading ? 'Saving...' : currentLoadedLineup ? `Save "${currentLoadedLineup.name}"` : 'Save Lineup'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={logout}>
                    Logout
                  </button>
                </>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => setAuthModalOpen(true)}>
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="app-toolbar">
        <div className="app-toolbar-inner">
          <nav className="app-tabs">
            <button
              type="button"
              className={`app-tab ${activeView === "players" ? "active" : ""}`}
              onClick={() => setActiveView("players")}
            >
              Players
            </button>
            <button
              type="button"
              className={`app-tab ${activeView === "lineup" ? "active" : ""}`}
              onClick={() => setActiveView("lineup")}
            >
              My Lineup
            </button>
            <button
              type="button"
              className={`app-tab ${activeView === "wishlist" ? "active" : ""}`}
              onClick={() => setActiveView("wishlist")}
            >
              Free Agents
              {wishlistPlayers.length + wishlistGoalies.length > 0 && (
                <span className="tab-badge">{wishlistPlayers.length + wishlistGoalies.length}</span>
              )}
            </button>
          </nav>

          <div className="sticky-nav-container">
            <div className="sticky-cap-free">
              <label className="form-label">Cap Free:</label>
              <span className={`cap-free-value ${totalSalary > salaryCap ? 'over-cap' : ''}`}>
                {totalSalary <= salaryCap 
                  ? `$${(salaryCap - totalSalary).toLocaleString('en-US')}`
                  : `-$${(totalSalary - salaryCap).toLocaleString('en-US')}`
                }
              </span>
            </div>
            
            <div className="sticky-roster-count">
              <label className="form-label">Roster:</label>
              <span className={`roster-count-value ${assignedPlayers.length > 23 ? 'over-limit' : ''}`}>
                {assignedPlayers.length}/23
              </span>
            </div>
            
            {isAuthenticated && (
              <div className="sticky-save-button">
                <button 
                  className="btn btn-primary btn-sm sticky-save-btn" 
                  onClick={handleQuickSave}
                  disabled={quickSaveLoading}
                >
                  {quickSaveLoading ? 'Saving...' : currentLoadedLineup ? `Save "${currentLoadedLineup.name}"` : 'Save Lineup'}
                </button>
              </div>
            )}
            
            <div className="sticky-theme-picker">
              <label className="form-label">Theme:</label>
              <select className="form-select" value={theme} onChange={e => setTheme(e.target.value)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="gray">Gray</option>
                <option value="black">Black</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {activeView === "players" && (
      <main className="main-content">

        {error && (
          <div className="card mb-4" style={{ borderColor: 'var(--danger)', background: 'rgba(239, 68, 68, 0.1)' }}>
            <div className="card-content">
              <p style={{ color: 'var(--danger)', margin: 0, fontWeight: 500 }}>{error}</p>
            </div>
          </div>
        )}

        {isLoadingRoster && <RosterLoadingSkeleton />}

        {!isLoadingRoster && dataUpdatedAt && (
          <div className="roster-data-stamp">
            Roster updated {dataUpdatedAt.toLocaleString()}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setCompareSeed(null);
                setCompareOpen(true);
              }}
            >
              Compare players
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">Player Filters</h2>
          </div>
          <div className="card-content">
            <div className="filters">
              <div className="form-group mb-0">
                <label className="form-label">Search</label>
                <div className="search-box">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search players..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label">Position</label>
                <select className="form-select" value={position} onChange={handlePositionFilter}>
                  <option value="all">All Players</option>
                  <option value="Center">Center</option>
                  <option value="Right Wing">Right Wing</option>
                  <option value="Left Wing">Left Wing</option>
                  <option value="Defenseman">Defenseman</option>
                  <option value="Goalie">Goalie</option>
                </select>
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label">Team</label>
                <select className="form-select" value={team} onChange={handleTeamFilter}>
                  <option value="all">All Teams</option>
                  {allTeams.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {position !== "Goalie" && (
                <div className="form-group mb-0">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={playerTypeFilter}
                    onChange={e => setPlayerTypeFilter(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="T">T - Transport</option>
                    <option value="P">P - Playmaker</option>
                    <option value="S">S - Scorer</option>
                  </select>
                </div>
              )}

              <div className="form-group mb-0">
                <label className="form-label">Sort by</label>
                <select className="form-select" value={sortColumn} onChange={e => setSortColumn(e.target.value)}>
                  {sortOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt === "playerType" ? "Type (T/P/S)" : opt}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group mb-0">
                <label className="form-label">Direction</label>
                <select className="form-select" value={sortDirection} onChange={handleSortDirectionChange}>
                  <option value="desc">High to Low</option>
                  <option value="asc">Low to High</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Player Table */}
        <div className="card mb-4">
          <div className="card-header">
            <h2 className="card-title">
              {position === "Goalie" ? "Goalies" : position === "all" ? "All Players" : position}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {position !== "Goalie" && (
                <div className="player-type-legend" style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      borderRadius: '50%',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>T</span>
                    <span>Transport</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      borderRadius: '50%',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>P</span>
                    <span>Playmaker</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      padding: '2px 6px',
                      borderRadius: '50%',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>S</span>
                    <span>Scorer</span>
                  </div>
                </div>
              )}
              <div className="view-toggle">
                {position === "Goalie" ? (
                <>
                  <button 
                    className={`toggle-btn ${goalieViewMode === 'cards' ? 'active' : ''}`}
                    onClick={() => setGoalieViewMode('cards')}
                  >
                    Cards
                  </button>
                  <button 
                    className={`toggle-btn ${goalieViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setGoalieViewMode('table')}
                  >
                    Table
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className={`toggle-btn ${playerViewMode === 'cards' ? 'active' : ''}`}
                    onClick={() => setPlayerViewMode('cards')}
                  >
                    Cards
                  </button>
                  <button 
                    className={`toggle-btn ${playerViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setPlayerViewMode('table')}
                  >
                    Table
                  </button>
                </>
                )}
              </div>
            </div>
          </div>
          <div className="table-container">
            {position === "Goalie" ? (
              filteredGoalies.length > 0 ? (
                goalieViewMode === 'cards' ? (
                  <VirtualizedPlayerGrid
                    items={filteredGoalies}
                    renderCard={renderGoalieCard}
                  />
                ) : (
                  <VirtualizedGoalieTable
                    goalies={filteredGoalies}
                    goalieRateColumns={goalieRateColumns}
                    rateMin={goalieRatingRange.min}
                    rateMax={goalieRatingRange.max}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onHeaderClick={handleGoalieHeaderClick}
                    assignmentMap={goalieAssignmentMap}
                    onPlayerClick={handlePlayerClick}
                    onAddToLineup={(player, e) => handlePlayerClick(e, player)}
                  />
                )
              ) : (
                <div className="card-content">
                  <p className="text-center text-muted">No goalies found matching your filters</p>
                </div>
              )
            ) : (
              filteredPlayers.length > 0 ? (
                playerViewMode === 'cards' ? (
                  <VirtualizedPlayerGrid
                    items={filteredPlayers}
                    renderCard={renderPlayerCard}
                  />
                ) : (
                  <VirtualizedPlayerTable
                    players={filteredPlayers}
                    rateColumns={rateColumns}
                    rateMin={playerRatingRange.min}
                    rateMax={playerRatingRange.max}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onHeaderClick={handleHeaderClick}
                    assignmentMap={playerAssignmentMap}
                    onPlayerClick={handlePlayerClick}
                    onAddToLineup={(player, e) => handlePlayerClick(e, player)}
                  />
                )
              ) : (
                <div className="card-content">
                  <p className="text-center text-muted">
                    {isLoadingRoster ? 'Loading players…' : 'No players found matching your filters'}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </main>
      )}

      {activeView === "lineup" && (
      <main className="main-content">
        {error && (
          <div className="card mb-4" style={{ borderColor: 'var(--danger)', background: 'rgba(239, 68, 68, 0.1)' }}>
            <div className="card-content">
              <p style={{ color: 'var(--danger)', margin: 0, fontWeight: 500 }}>{error}</p>
            </div>
          </div>
        )}

        <div className="card" id="lineup">
          <div className="card-header lineup-card-header">
            <h2 className="card-title">My Lineup</h2>
            <div className="lineup-card-header-actions">
              {isAuthenticated && (
                <div className="user-team-controls">
                  <label htmlFor="user-team-select" className="form-label">
                    Your team
                  </label>
                  <select
                    id="user-team-select"
                    className="form-select"
                    value={selectedUserTeam}
                    onChange={handleUserTeamChange}
                  >
                    <option value="">Select team...</option>
                    {rosterTeamOptions.map((teamName) => (
                      <option key={teamName} value={teamName}>
                        {teamName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleLoadTeamRoster}
                    disabled={!selectedUserTeam}
                    title="Fill lineup with rostered skaters (AF >= 1) and goalies (Z >= 1) from your team"
                  >
                    Load Roster
                  </button>
                </div>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={undoLineupChange}
                disabled={undoCount === 0}
                title="Undo last lineup change"
              >
                Undo{undoCount > 0 ? ` (${undoCount})` : ''}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setCompareSeed(null);
                  setCompareOpen(true);
                }}
                title="Compare any two players"
              >
                Compare
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={copyLineup}
                title="Copy lineup to clipboard"
              >
                Copy text
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={downloadLineupTxt}
                title="Download lineup as .txt"
              >
                .txt
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={downloadLineupPng}
                title="Download lineup as image"
              >
                Image
              </button>
            </div>
          </div>

          {exportNotice ? (
            <div className="lineup-export-notice">{exportNotice}</div>
          ) : null}

          <LineupAnalysisPanel
            forwards={forwards}
            defensemen={defensemen}
            lineupGoalies={lineupGoalies}
            totalSalary={totalSalary}
            salaryCap={salaryCap}
          />
          
          {/* Salary Cap */}
          <div className="salary-cap">
            <div className="salary-cap-header">
              <h3 className="salary-cap-title">Salary Cap Management</h3>
              <div className="form-group mb-0" style={{ width: '200px' }}>
                <label className="form-label">Salary Cap</label>
                <input
                  type="number"
                  className="form-input"
                  value={salaryCap}
                  onChange={e => {
                    setSalaryCap(Number(e.target.value));
                    // Keep currentLoadedLineup for quick save functionality
                  }}
                />
              </div>
              <div className="form-group mb-0" style={{ width: '200px' }}>
                <label className="form-label">Retained Salary</label>
                <input
                  type="number"
                  className="form-input"
                  value={retainedSalary}
                  placeholder="$0"
                  onChange={e => {
                    setRetainedSalary(Number(e.target.value) || 0);
                    // Keep currentLoadedLineup for quick save functionality
                  }}
                />
              </div>
            </div>
            
            <div className="salary-progress">
              <div 
                className={`salary-progress-bar ${totalSalary > salaryCap ? 'over-cap' : ''}`}
                style={{ width: `${Math.min((totalSalary / salaryCap) * 100, 100)}%` }}
              ></div>
            </div>
            
            <div className="salary-details">
              <span>Players: ${(totalSalary - retainedSalary).toLocaleString('en-US')}</span>
              {retainedSalary > 0 && (
                <span>Retained: ${retainedSalary.toLocaleString('en-US')}</span>
              )}
              <span>Total Used: ${totalSalary.toLocaleString('en-US')}</span>
              <span>Cap: ${salaryCap.toLocaleString('en-US')}</span>
              {totalSalary <= salaryCap ? (
                <span>Cap Free: ${(salaryCap - totalSalary).toLocaleString('en-US')}</span>
              ) : (
                <span className="salary-over">OVER CAP by ${(totalSalary - salaryCap).toLocaleString('en-US')}</span>
              )}
            </div>
          </div>

          {/* Roster Count */}
          <div className="roster-count-section">
            <div className="roster-count-header">
              <h3>Roster Count</h3>
              <div className={`roster-count ${assignedPlayers.length > 23 ? 'over-limit' : ''}`}>
                {assignedPlayers.length}/23
              </div>
            </div>
            
            {/* Position Breakdown */}
            {assignedPlayers.length > 0 && (
              <div className="position-breakdown">
                {Object.entries(getPositionBreakdown())
                  .sort(([a], [b]) => {
                    // Sort order: G, then alphabetically by position
                    if (a === 'G') return -1;
                    if (b === 'G') return 1;
                    return a.localeCompare(b);
                  })
                  .map(([position, count]) => (
                    <span key={position} className="position-count">
                      {position}: {count}
                    </span>
                  ))
                }
              </div>
            )}
            
            {assignedPlayers.length > 23 && (
              <div className="roster-warning">
                OVER ROSTER LIMIT by {assignedPlayers.length - 23}
              </div>
            )}
          </div>

          <div className="card-content">
            <div className="lineup-section">
              <h3 className="lineup-section-title">Forwards (4 lines: LW, C, RW)</h3>
              {(() => {
                // Calculate min/max across ALL forward line averages for consistent color scale
                const allForwardAverages = [];
                forwards.forEach(line => {
                  const linePlayers = getLinePlayers(line, true);
                  if (linePlayers.length > 0) {
                    const lineAverages = getLineupAverages(linePlayers, rateColumns);
                    rateColumns.forEach(rate => {
                      allForwardAverages.push(lineAverages[rate] || 0);
                    });
                  }
                });
                const globalMin = allForwardAverages.length > 0 ? Math.min(...allForwardAverages) : 0;
                const globalMax = allForwardAverages.length > 0 ? Math.max(...allForwardAverages) : 0;
                
                return forwards.map((line, lineIdx) => {
                  const linePlayers = getLinePlayers(line, true);
                  const lineAverages = getLineupAverages(linePlayers, rateColumns);
                  const chemistry = getLineChemistry(Object.values(line).filter(Boolean));
                return (
                  <div className="lineup-line" key={`forwards-${lineIdx}`}>
                    <div className="lineup-line-header">
                      <span>{lineIdx === 4 ? "Extra FW" : `Line ${lineIdx + 1}`}</span>
                      <LineChemistryBadge chemistry={chemistry} />
                    </div>
                    <div className="lineup-positions">
                      {Object.entries(line).map(([pos, player]) => (
                        <div 
                          key={`forwards-${lineIdx}-${pos}`} 
                          className={`position-slot ${!player ? 'empty' : ''} ${draggedPlayer ? 'drag-active' : ''}`}
                          onDragOver={(e) => handleDragOver(e, { type: 'forwards', lineIndex: lineIdx, position: pos })}
                          onDrop={(e) => handleDrop(e, { type: 'forwards', lineIndex: lineIdx, position: pos })}
                        >
                          <div>
                            <div className="position-label">
                              {lineIdx === 4 ? 
                                (pos === 'LW' ? 'Extra FW1' : pos === 'C' ? 'Extra FW2' : 'Extra FW3') 
                                : (lineupPositionMap[pos] || pos)
                              }
                            </div>
                            {player ? (
                              <div 
                                className={`player-info draggable-player ${expandedPlayers.has(player.id) ? 'expanded' : ''}`}
                                draggable={true}
                                onDragStart={(e) => handleDragStart(e, player, { type: 'forwards', lineIndex: lineIdx, position: pos })}
                                onDragEnd={handleDragEnd}
                                onClick={(e) => handleLineupPlayerClick(e, player)}
                                style={{ cursor: 'pointer' }}
                              >
                                {renderPlayerContent(player)}
                              </div>
                            ) : (
                              <div className="player-info drop-zone position-search">
                                {(() => {
                                  const positionKey = getPositionKey('forwards', lineIdx, pos);
                                  const searchTerm = positionSearches[positionKey] || '';
                                  const searchResults = positionSearchResults[positionKey] || [];
                                  
                                  return (
                                    <>
                                      <input
                                        type="text"
                                        placeholder="Search players..."
                                        value={searchTerm}
                                        onChange={(e) => handlePositionSearch(positionKey, e.target.value, 'forwards', lineIdx, pos)}
                                        className="position-search-input"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      {searchResults.length > 0 && (
                                        <div className="position-search-results">
                                          {searchResults.map(player => (
                                            <div
                                              key={player.id}
                                              className="position-search-result"
                                              onClick={() => handlePlayerSelect(player, 'forwards', lineIdx, pos)}
                                            >
                                              <div className="search-result-name">{player.name}</div>
                                              <div className="search-result-cap">
                                                ${Number(player.salary || 0).toLocaleString('en-US')}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          {player && !expandedPlayers.has(player.id) && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                className="form-input"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                placeholder="$0"
                                style={{ width: "80px", fontSize: '0.75rem' }}
                                value={salaries[player.id] ? `$${Number(salaries[player.id].replace(/[^0-9]/g, '')).toLocaleString('en-US')}` : ''}
                                onChange={e => {
                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                  setSalaries(s => ({ ...s, [player.id]: raw }));
                                  // Keep currentLoadedLineup for quick save functionality
                                }}
                              />
                              <button className="btn btn-danger btn-sm" onClick={() => handleAssignment(player, "unassign")}>
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {linePlayers.length > 0 && (
                      <div className="lineup-line-averages">
                        <div>Line Averages:</div>
                        <div className="averages-grid">
                          {rateColumns.map(rate => {
                            const value = lineAverages[rate] || 0;
                            const backgroundColor = getRatingColor(value, globalMin, globalMax);
                              
                              return (
                                <div 
                                  key={rate} 
                                  className="average-stat"
                                  style={{
                                    backgroundColor,
                                    borderColor: backgroundColor
                                  }}
                                >
                                  <div className="average-stat-label">{rate}</div>
                                  <div className="average-stat-value">{value || "-"}</div>
                                </div>
                              );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
                });
              })()}
            </div>

            <div className="lineup-section">
              <h3 className="lineup-section-title">Defensemen (4 lines: D1, D2)</h3>
              {(() => {
                // Calculate min/max across ALL defensemen line averages for consistent color scale
                const allDefenseAverages = [];
                defensemen.forEach(line => {
                  const linePlayers = getLinePlayers(line, false);
                  if (linePlayers.length > 0) {
                    const lineAverages = getLineupAverages(linePlayers, rateColumns);
                    rateColumns.forEach(rate => {
                      allDefenseAverages.push(lineAverages[rate] || 0);
                    });
                  }
                });
                const globalMin = allDefenseAverages.length > 0 ? Math.min(...allDefenseAverages) : 0;
                const globalMax = allDefenseAverages.length > 0 ? Math.max(...allDefenseAverages) : 0;
                
                return defensemen.map((line, lineIdx) => {
                  const linePlayers = getLinePlayers(line, false);
                  const lineAverages = getLineupAverages(linePlayers, rateColumns);
                  const chemistry = getLineChemistry(Object.values(line).filter(Boolean));
                return (
                  <div className="lineup-line" key={`defensemen-${lineIdx}`}>
                    <div className="lineup-line-header">
                      <span>{lineIdx === 3 ? "Extra D" : `Line ${lineIdx + 1}`}</span>
                      <LineChemistryBadge chemistry={chemistry} />
                    </div>
                    <div className="lineup-positions">
                      {Object.entries(line).map(([pos, player]) => (
                        <div 
                          key={`defensemen-${lineIdx}-${pos}`} 
                          className={`position-slot ${!player ? 'empty' : ''} ${draggedPlayer ? 'drag-active' : ''}`}
                          onDragOver={(e) => handleDragOver(e, { type: 'defensemen', lineIndex: lineIdx, position: pos })}
                          onDrop={(e) => handleDrop(e, { type: 'defensemen', lineIndex: lineIdx, position: pos })}
                        >
                          <div>
                            <div className="position-label">{lineupPositionMap[pos] || pos}</div>
                            {player ? (
                              <div 
                                className={`player-info draggable-player ${expandedPlayers.has(player.id) ? 'expanded' : ''}`}
                                draggable={true}
                                onDragStart={(e) => handleDragStart(e, player, { type: 'defensemen', lineIndex: lineIdx, position: pos })}
                                onDragEnd={handleDragEnd}
                                onClick={(e) => handleLineupPlayerClick(e, player)}
                                style={{ cursor: 'pointer' }}
                              >
                                {renderPlayerContent(player)}
                              </div>
                            ) : (
                              <div className="player-info drop-zone position-search">
                                {(() => {
                                  const positionKey = getPositionKey('defensemen', lineIdx, pos);
                                  const searchTerm = positionSearches[positionKey] || '';
                                  const searchResults = positionSearchResults[positionKey] || [];
                                  
                                  return (
                                    <>
                                      <input
                                        type="text"
                                        placeholder="Search players..."
                                        value={searchTerm}
                                        onChange={(e) => handlePositionSearch(positionKey, e.target.value, 'defensemen', lineIdx, pos)}
                                        className="position-search-input"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      {searchResults.length > 0 && (
                                        <div className="position-search-results">
                                          {searchResults.map(player => (
                                            <div
                                              key={player.id}
                                              className="position-search-result"
                                              onClick={() => handlePlayerSelect(player, 'defensemen', lineIdx, pos)}
                                            >
                                              <div className="search-result-name">{player.name}</div>
                                              <div className="search-result-cap">
                                                ${Number(player.salary || 0).toLocaleString('en-US')}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          {player && !expandedPlayers.has(player.id) && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                className="form-input"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                placeholder="$0"
                                style={{ width: "80px", fontSize: '0.75rem' }}
                                value={salaries[player.id] ? `$${Number(salaries[player.id].replace(/[^0-9]/g, '')).toLocaleString('en-US')}` : ''}
                                onChange={e => {
                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                  setSalaries(s => ({ ...s, [player.id]: raw }));
                                  // Keep currentLoadedLineup for quick save functionality
                                }}
                              />
                              <button className="btn btn-danger btn-sm" onClick={() => handleAssignment(player, "unassign")}>
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {linePlayers.length > 0 && (
                      <div className="lineup-line-averages">
                        <div>Line Averages:</div>
                        <div className="averages-grid">
                          {rateColumns.map(rate => {
                            const value = lineAverages[rate] || 0;
                            const backgroundColor = getRatingColor(value, globalMin, globalMax);
                              
                              return (
                                <div 
                                  key={rate} 
                                  className="average-stat"
                                  style={{
                                    backgroundColor,
                                    borderColor: backgroundColor
                                  }}
                                >
                                  <div className="average-stat-label">{rate}</div>
                                  <div className="average-stat-value">{value || "-"}</div>
                                </div>
                              );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
                });
              })()}
            </div>
            
            {/* Goalies Section */}
            <div className="lineup-section">
              <h3 className="lineup-section-title">Goalies</h3>
              <div className="lineup-line">
                <div className="lineup-line-header">Goalie Lineup</div>
                <div className="lineup-positions">
                  {lineupGoalies.map((goalie, index) => (
                    <div 
                      key={index} 
                      className={`position-slot ${!goalie ? 'empty' : ''} ${draggedPlayer ? 'drag-active' : ''}`}
                      onDragOver={(e) => handleDragOver(e, { type: 'goalie', index })}
                      onDrop={(e) => handleDrop(e, { type: 'goalie', index })}
                    >
                      <div>
                        <div className="position-label">G{index + 1}</div>
                        {goalie ? (
                          <div 
                            className={`player-info draggable-player ${expandedPlayers.has(goalie.id) ? 'expanded' : ''}`}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, goalie, { type: 'goalie', index })}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => handleLineupPlayerClick(e, goalie)}
                            style={{ cursor: 'pointer' }}
                          >
                            {renderPlayerContent(goalie)}
                          </div>
                        ) : (
                          <div className="player-info drop-zone position-search">
                            {(() => {
                              const positionKey = getPositionKey('goalie', null, null, index);
                              const searchTerm = positionSearches[positionKey] || '';
                              const searchResults = positionSearchResults[positionKey] || [];
                              
                              return (
                                <>
                                  <input
                                    type="text"
                                    placeholder="Search goalies..."
                                    value={searchTerm}
                                    onChange={(e) => handlePositionSearch(positionKey, e.target.value, 'goalie', null, null)}
                                    className="position-search-input"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  {searchResults.length > 0 && (
                                    <div className="position-search-results">
                                      {searchResults.map(player => (
                                        <div
                                          key={player.id}
                                          className="position-search-result"
                                          onClick={() => handlePlayerSelect(player, 'goalie', null, null, index)}
                                        >
                                          <div className="search-result-name">{player.name}</div>
                                          <div className="search-result-cap">
                                            ${Number(player.salary || 0).toLocaleString('en-US')}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      {goalie && !expandedPlayers.has(goalie.id) && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="text"
                            className="form-input"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="$0"
                            style={{ width: "80px", fontSize: '0.75rem' }}
                            value={salaries[goalie.id] ? `$${Number(salaries[goalie.id].replace(/[^0-9]/g, '')).toLocaleString('en-US')}` : ''}
                            onChange={e => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              setSalaries(prev => ({ ...prev, [goalie.id]: value }));
                              // Keep currentLoadedLineup for quick save functionality
                            }}
                          />
                          <button
                            className="btn btn-remove"
                            style={{ padding: '0.25rem', fontSize: '0.75rem' }}
                            onClick={() => handleAssignment(goalie, "unassign")}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {(() => {
                  const goalieList = lineupGoalies.filter(Boolean);
                  if (goalieList.length === 0) return null;
                  
                  // Calculate averages for goalies (they have nested .rates structure)
                  const averages = {};
                  goalieRateColumns.forEach(col => {
                    const sum = goalieList.reduce((acc, g) => acc + (g[col.name] || 0), 0);
                    averages[col.name] = Math.round(sum / goalieList.length);
                  });
                  const lineAverages = averages;
                  
                  // Separate OV from other goalie ratings for different color scales
                  const regularGoalieColumns = goalieRateColumns.filter(col => col.name !== 'OV');
                  const ovColumn = goalieRateColumns.find(col => col.name === 'OV');
                  
                  // Color scale for regular goalie ratings (excluding OV)
                  const regularAverageValues = regularGoalieColumns.map(col => lineAverages[col.name] || 0);
                  const regularMin = Math.min(...regularAverageValues);
                  const regularMax = Math.max(...regularAverageValues);
                  
                  // Color scale for OV based on all goalies' OV values
                  const allGoalieOVValues = goalies.map(g => g.rates?.OV || 0).filter(v => v > 0);
                  const ovMin = allGoalieOVValues.length > 0 ? Math.min(...allGoalieOVValues) : 0;
                  const ovMax = allGoalieOVValues.length > 0 ? Math.max(...allGoalieOVValues) : 100;
                  
                  return (
                    <div className="lineup-line-averages">
                      <div>Goalie Averages:</div>
                      <div className="averages-grid">
                        {/* Render regular goalie rating averages */}
                        {regularGoalieColumns.map(col => {
                          const value = lineAverages[col.name] || 0;
                          const backgroundColor = getRatingColor(value, regularMin, regularMax);
                          
                          return (
                            <div 
                              key={col.name} 
                              className="average-stat"
                              style={{
                                backgroundColor,
                                borderColor: backgroundColor
                              }}
                            >
                              <div className="average-stat-label">{col.name}</div>
                              <div className="average-stat-value">{value || "-"}</div>
                            </div>
                          );
                        })}
                        
                        {/* Render OV average with its own color scale */}
                        {ovColumn && (
                          <div 
                            key={ovColumn.name} 
                            className="average-stat"
                            style={{
                              backgroundColor: getRatingColor(lineAverages[ovColumn.name] || 0, ovMin, ovMax),
                              borderColor: getRatingColor(lineAverages[ovColumn.name] || 0, ovMin, ovMax)
                            }}
                          >
                            <div className="average-stat-label">{ovColumn.name}</div>
                            <div className="average-stat-value">{lineAverages[ovColumn.name] || "-"}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </main>
      )}

      {activeView === "wishlist" && (
        <main className="main-content">
          <FreeAgentWishlist
            wishlistPlayers={wishlistPlayers}
            wishlistGoalies={wishlistGoalies}
            wishlistSalaries={wishlistSalaries}
            setWishlistPlayers={setWishlistPlayers}
            setWishlistGoalies={setWishlistGoalies}
            setWishlistSalaries={setWishlistSalaries}
            players={players}
            goalies={goalies}
            forwards={forwards}
            defensemen={defensemen}
            lineupGoalies={lineupGoalies}
            salaries={salaries}
            retainedSalary={retainedSalary}
            salaryCap={salaryCap}
            toggleWishlist={toggleWishlist}
            isInWishlist={isInWishlist}
            onGoToLineup={() => setActiveView("lineup")}
            onAddToLineup={openAssignmentMenu}
            getLineupAssignmentDisplay={getLineupAssignmentDisplay}
            skaterRatingRange={playerRatingRange}
            goalieRatingRange={goalieRatingRange}
          />
        </main>
      )}

      {/* Auth Modal */}
      {authModalOpen && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            onSuccess={() => devLog('Authentication successful')}
          />
        </Suspense>
      )}

      {/* Lineup Manager Modal */}
      {lineupModalOpen && (
        <Suspense fallback={null}>
          <LineupManagerModal
            isOpen={lineupModalOpen}
            onClose={() => setLineupModalOpen(false)}
            forwards={forwards}
            defensemen={defensemen}
            lineupGoalies={lineupGoalies}
            salaries={salaries}
            salaryCap={salaryCap}
            retainedSalary={retainedSalary}
            onLoadLineup={handleLoadLineup}
            currentLoadedLineup={currentLoadedLineup}
          />
        </Suspense>
      )}

      {/* Context Menu */}
      {contextMenu.visible && contextMenu.player && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">
            <strong>{contextMenu.player.name}</strong>
            <span className="context-menu-position">
              {goalies.some(g => g.id === contextMenu.player.id) ? "G" : contextMenu.player.positionDisplay}
            </span>
          </div>
          
          {(() => {
            const isGoalie = goalies.some(g => g.id === contextMenu.player.id);
            const isCurrentlyAssigned = isGoalie ? isGoalieAssigned(contextMenu.player) : isAssigned(contextMenu.player);
            
            return isCurrentlyAssigned ? (
              <div className="context-menu-section">
                <div className="context-menu-current">
                  Currently: {isGoalie 
                    ? getGoalieAssignmentLabel(getCurrentGoalieAssignment(contextMenu.player))
                    : getAssignmentLabel(getCurrentAssignment(contextMenu.player))
                  }
                </div>
                <button 
                  className="context-menu-item unassign"
                  onClick={() => handleContextMenuAssignment("unassign")}
                >
                  ✕ Remove from lineup
                </button>
              </div>
            ) : (
              <div className="context-menu-section">
                <div className="context-menu-label">Assign to:</div>
                {(isGoalie 
                  ? getGoalieAssignmentOptions(contextMenu.player) 
                  : getAssignmentOptions(contextMenu.player)
                ).map(option => (
                  <button
                    key={option.value}
                    className="context-menu-item"
                    onClick={() => handleContextMenuAssignment(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}


      {/* Quick Save Prompt Modal */}
      {quickSavePromptOpen && (
        <div className="lineup-modal-overlay" onClick={() => setQuickSavePromptOpen(false)}>
          <div className="lineup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="lineup-modal-header">
              <h2>Save Lineup</h2>
              <button className="close-btn" onClick={() => setQuickSavePromptOpen(false)}>×</button>
            </div>
            <div className="lineup-modal-content">
              <div className="save-form">
                <input
                  type="text"
                  placeholder="Enter lineup name..."
                  value={quickSaveLineupName}
                  onChange={(e) => setQuickSaveLineupName(e.target.value)}
                  disabled={quickSaveLoading}
                  maxLength={50}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleQuickSaveWithName();
                    }
                  }}
                />
                <button 
                  onClick={handleQuickSaveWithName}
                  disabled={quickSaveLoading || !quickSaveLineupName.trim()}
                >
                  {quickSaveLoading ? 'Saving...' : 'Save Lineup'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PlayerCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        players={players}
        goalies={goalies}
        initialPlayerA={compareSeed}
      />
    </div>
  );
}

export default HockeyLineupBuilder;
